const MSK_TIMEZONE = 'Europe/Moscow';

export const getMskCalendarParts = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MSK_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  return {
    year: Number(parts.find((part) => part.type === 'year')?.value),
    month: Number(parts.find((part) => part.type === 'month')?.value),
    day: Number(parts.find((part) => part.type === 'day')?.value),
  };
};

export const createMskCalendarDate = (year, month, day) => {
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

export const getMskToday = () => {
  const parts = getMskCalendarParts(new Date());
  return createMskCalendarDate(parts.year, parts.month, parts.day);
};

export const normalizeMskCalendarDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  return createMskCalendarDate(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
  );
};

export const addMskCalendarDays = (date, days) => {
  const normalizedDate = normalizeMskCalendarDate(date);
  if (!normalizedDate) {
    return null;
  }

  const nextDate = new Date(normalizedDate);
  nextDate.setDate(nextDate.getDate() + days);
  return normalizeMskCalendarDate(nextDate);
};

export const getMskRangeSpanDays = (rangeStart, rangeEnd) => {
  const normalizedStart = normalizeMskCalendarDate(rangeStart);
  const normalizedEnd = normalizeMskCalendarDate(rangeEnd);

  if (!normalizedStart || !normalizedEnd) {
    return 7;
  }

  return Math.max(
    1,
    Math.round((normalizedEnd.getTime() - normalizedStart.getTime()) / 86400000) + 1,
  );
};

export const formatMskApiDate = (date) => {
  const normalizedDate = normalizeMskCalendarDate(date);
  if (!normalizedDate) {
    return null;
  }

  const year = normalizedDate.getFullYear();
  const month = String(normalizedDate.getMonth() + 1).padStart(2, '0');
  const day = String(normalizedDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatMskShortDate = (date) => {
  const normalizedDate = normalizeMskCalendarDate(date);
  if (!normalizedDate) {
    return '';
  }

  const day = String(normalizedDate.getDate()).padStart(2, '0');
  const month = String(normalizedDate.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
};

const hasExplicitTimezone = (value) => /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);

// Москва без DST с 2014. Naive с бэка — стенные часы МСК, не UTC.
const MSK_OFFSET = '+03:00';

export const parseMskInstant = (raw) => {
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw;
  }
  if (typeof raw === 'number') {
    const parsed = new Date(raw > 1e12 ? raw : raw * 1000);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof raw !== 'string') {
    return null;
  }

  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const baseIso = trimmed.includes(' ') && !trimmed.includes('T')
    ? trimmed.replace(' ', 'T')
    : trimmed;
  const normalized = hasExplicitTimezone(baseIso) ? baseIso : `${baseIso}${MSK_OFFSET}`;
  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  const fallback = new Date(trimmed);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

/** @deprecated имя историческое: naive больше не UTC. Используй parseMskInstant. */
export const parseUtcInstant = parseMskInstant;

export const formatMskDateTime = (raw) => {
  const date = parseMskInstant(raw);
  if (!date) {
    return '—';
  }

  return date.toLocaleString('ru-RU', {
    timeZone: MSK_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

export const formatMskDateRangeLabel = (rangeStart, rangeEnd) => {
  const spanDays = getMskRangeSpanDays(rangeStart, rangeEnd);
  if (spanDays === 1) {
    return formatMskShortDate(rangeStart);
  }

  return `${formatMskShortDate(rangeStart)} - ${formatMskShortDate(rangeEnd)}`;
};
